import apiClient from '../../shared/api/client';
import type { Event } from '../events/endpoints';

export interface Course {
  id: number;
  name: string;
  description: string;
  /** Legacy single-division field. The backend keeps this in sync with
   *  `divisions[0]` so old API consumers keep working. Prefer `divisions`
   *  in new UI code. */
  division: string;
  /** Multi-division: a course can belong to more than one audience lane
   *  (e.g. ["private_sector", "hr"] for a course used by both LP and HR
   *  teams). The backend guarantees at least one entry. */
  divisions: string[];
  delivery_type: string;
  program_type: string | null;
  tags: string[];
  duration_days: number;
  /** How long a learner keeps access from the day the course is assigned; null = never expires. */
  access_days?: number | null;
  price_cents: number;
  early_price_cents: number | null;
  late_price_cents: number | null;
  /** Shown on the course card: CEUs earned, approximate length in hours, and
   *  the bodies (SHRM, HRCI) that approved the course for credits. */
  ceus: number | null;
  approx_hours: number | null;
  credit_approvals: string[];
  /** w-z.com's On-Demand page: whether the course is listed there, whether an
   *  unpublished one shows as "Coming soon", and its language ("en" | "es"). */
  on_demand_listed: boolean;
  coming_soon: boolean;
  language: string;
  currency: string;
  active: boolean;
  image_url: string | null;
  slug: string | null;
  thinkific_course_id: number | null;
  virtual_sapiens_required: boolean;
  virtual_sapiens_scenario_id: string | null;
  archived_at: string | null;
  archived: boolean;
  has_lms: boolean;
  has_events: boolean;
  upcoming_event_count: number;
  event_count: number;
  events?: Event[];
}

export interface ArchiveCourseResponse {
  course: Course;
  events_moved: number;
  target_course: { id: number; name: string } | null;
}

export interface CreateCourseParams {
  name: string;
  description: string;
  /** Legacy. New code should write `divisions` instead — the backend keeps
   *  `division` in sync with `divisions[0]` automatically. We still permit
   *  this field for callers that haven't migrated. */
  division?: string;
  /** New canonical divisions array. Must contain at least one known value. */
  divisions: string[];
  delivery_type: string;
  program_type?: string | null;
  tags?: string[];
  duration_days: number;
  /** How long a learner keeps access from the day the course is assigned; null = never expires. */
  access_days?: number | null;
  price_cents: number;
  early_price_cents?: number | null;
  late_price_cents?: number | null;
  ceus?: number | null;
  approx_hours?: number | null;
  credit_approvals?: string[];
  on_demand_listed?: boolean;
  coming_soon?: boolean;
  language?: string;
  currency: string;
  active: boolean;
  thinkific_course_id: number | null;
  virtual_sapiens_required?: boolean;
  virtual_sapiens_team_token?: string | null;
  virtual_sapiens_scenario_id?: string | null;
  image?: File | null;
  has_lms?: boolean;
  has_events?: boolean;
  upcoming_event_count?: number;
  events?: Event[];
}

function toFormData(params: Partial<CreateCourseParams>): FormData {
  const fd = new FormData();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (value instanceof File) {
      fd.append(key, value);
    } else if (value === null) {
      fd.append(key, '');
    } else if (typeof value === 'boolean') {
      fd.append(key, value ? 'true' : 'false');
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        fd.append(`${key}[]`, '');
      } else {
        value.forEach((v) => fd.append(`${key}[]`, String(v)));
      }
    } else if (typeof value === 'object') {
      continue;
    } else {
      fd.append(key, String(value));
    }
  }
  return fd;
}

function hasFile(params: Partial<CreateCourseParams>): boolean {
  return params.image instanceof File;
}

function multipartConfig(useMultipart: boolean) {
  return useMultipart ? { headers: { 'Content-Type': undefined as unknown as string } } : undefined;
}

export interface Enrollment {
  id: number;
  status: string;
  purchased_at: string | null;
  payment_intent_id: string | null;
  organization_id: number | null;
  progress_percentage: number;
  completed_at: string | null;
  thinkific_completed_at: string | null;
  virtual_sapiens_assessment_status: 'not_started' | 'started' | 'completed' | 'passed' | 'failed';
  virtual_sapiens_assessment_completed_at: string | null;
  virtual_sapiens_last_event_at: string | null;
  launch_url: string | null;
  course: Course;
}

export interface EventRegistrationLite {
  id: number;
  event_id: number;
  status: string;
  payment_method: string | null;
  price_cents_paid: number | null;
  registered_at: string;
  checked_in_at: string | null;
  cancelled_at: string | null;
  event: Event;
}

export interface OrgPackage {
  id: number;
  seats_purchased: number;
  seats_assigned: number;
  seats_available: number;
  status: string;
  purchased_at: string | null;
  course: Course;
  enrollments: Array<{
    id: number;
    status: string;
    progress_percentage: number;
    completed_at: string | null;
    user: { id: number; email_address: string; full_name: string };
  }>;
  event_registrations: Array<{
    id: number;
    status: string;
    event: { id: number; display_title: string; starts_at: string; city: string | null };
    user: { id: number; email_address: string; full_name: string };
  }>;
}

export interface OrgEventRegistration {
  id: number;
  status: string;
  registered_at: string;
  checked_in_at: string | null;
  cancelled_at: string | null;
  payment_method: string | null;
  price_cents_paid: number | null;
  event: {
    id: number;
    display_title: string;
    starts_at: string;
    ends_at: string | null;
    format: string;
    city: string | null;
    state: string | null;
    location: string | null;
    virtual_url: string | null;
    timezone: string | null;
  };
  course: { id: number; name: string; division: string };
  user: { id: number; email_address: string; full_name: string };
  registered_by: { id: number; email_address: string; full_name: string } | null;
}

export interface EnrollmentsResponse {
  total_records: number;
  result: Enrollment[];
  event_registrations: EventRegistrationLite[];
  org_packages: OrgPackage[];
  org_registrations: OrgEventRegistration[];
}

export const courseApi = {
  // `include_inactive` is honoured for system admins only: it adds courses not
  // yet published in Thinkific, which is where "Coming soon" gets set.
  list: async (params?: { division?: string; delivery_type?: string; has_lms?: boolean; include_inactive?: boolean }): Promise<Course[]> => {
    const { data } = await apiClient.get('/courses', { params });
    return data.result;
  },

  get: async (id: number | string): Promise<Course> => {
    const { data } = await apiClient.get(`/courses/${id}`);
    return data;
  },

  create: async (params: CreateCourseParams): Promise<Course> => {
    const useMultipart = hasFile(params);
    const body = useMultipart ? toFormData(params) : params;
    const { data } = await apiClient.post('/courses', body, multipartConfig(useMultipart));
    return data;
  },

  update: async (id: number | string, params: Partial<CreateCourseParams>): Promise<Course> => {
    const useMultipart = hasFile(params);
    const body = useMultipart ? toFormData(params) : params;
    const { data } = await apiClient.patch(`/courses/${id}`, body, multipartConfig(useMultipart));
    return data;
  },

  // Soft-archives the course. If `moveEventsToCourseId` is provided, every
  // event attached to the source is re-pointed to that course inside the
  // same transaction before archiving.
  archive: async (
    id: number | string,
    opts?: { moveEventsToCourseId?: number | null },
  ): Promise<ArchiveCourseResponse> => {
    const params: Record<string, number> = {};
    if (opts?.moveEventsToCourseId) params.move_events_to_course_id = opts.moveEventsToCourseId;
    const { data } = await apiClient.delete(`/courses/${id}`, { params });
    return data;
  },

  checkout: async (courseId: number | string, opts?: {
    discount_code?: string;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post(`/courses/${courseId}/checkout`, opts);
    return data;
  },
};

/**
 * The client default is two minutes, which for a click-and-wait screen is the
 * same as forever — a learner sees an endless spinner and gives up long before
 * any error appears (WPDS-174).
 *
 * Not shorter than this, though: the first launch for a learner with no
 * Thinkific account yet links one on the way through, and ThinkificClient
 * allows 10s to connect plus 30s to read. 45 leaves headroom over that worst
 * legitimate case while cutting the dead-end wait by nearly two thirds. The
 * screen also narrates the wait, so the time is not silent.
 */
export const LAUNCH_TIMEOUT_MS = 45_000;

export const enrollmentApi = {
  list: async (): Promise<EnrollmentsResponse> => {
    const { data } = await apiClient.get('/enrollments');
    return data;
  },

  /** Signs the learner into the LMS hub itself — no course needed, so it works
   *  for someone with nothing enrolled yet, who is who the hub's offers are for. */
  hubUrl: async (): Promise<{ url: string }> => {
    const { data } = await apiClient.get('/enrollments/hub_url', { timeout: LAUNCH_TIMEOUT_MS });
    return data;
  },

  launchUrl: async (enrollmentId: number | string): Promise<{ url: string }> => {
    const { data } = await apiClient.get(`/enrollments/${enrollmentId}/launch_url`, {
      timeout: LAUNCH_TIMEOUT_MS,
    });
    return data;
  },

  sync: async (): Promise<EnrollmentsResponse> => {
    const { data } = await apiClient.post('/enrollments');
    return data;
  },

  leave: async (enrollmentId: number | string): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/enrollments/${enrollmentId}`);
    return data;
  },

  selfEnroll: async (courseSlug: string): Promise<{
    enrollment_id: number;
    course: { id: number; name: string; slug: string | null };
    created: boolean;
  }> => {
    const { data } = await apiClient.post('/enrollments/self_enroll', { course_slug: courseSlug });
    return data;
  },
};

export const PENDING_SELF_ENROLL_COURSE_KEY = 'wz.pendingSelfEnrollCourseSlug';
